/* eslint-disable max-lines -- Pre-existing violation */
import {Helmet} from '@dr.pogodin/react-helmet';
import {
  BaseFlagProps,
  FlagProps,
  openConfirmDialog,
  showNotification,
  AlertProps,
} from 'platform/components';
import {HStack, Show, Space, Text} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {Fragment, ReactNode} from 'react';

import {always, defaultTo, equals, isNil, not} from 'ramda';
import {concatAll, isFalsy, isNilOrEmpty, isPositive} from 'ramda-adjunct';

import {
  useGetBranchListQuery,
  useGetDocumentContextCountQuery,
  useGetTenantQuery,
} from '@dms/api/core';
import {useGetCurrentUserInfoQuery, useGetUserQuery} from '@dms/api/customer';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  useGetRecordHeaderCollapsedQuery,
  useSetRecordHeaderCollapsedMutation,
} from '@dms/api/settings';
import {EntityResourceIds} from '@dms/api/shared';
import {
  GetDirectSaleResponse,
  GetWarehousesResponse,
  PatchDirectSaleRequest,
  useGetDirectSaleQuery,
  useGetDirectSaleTypesQuery,
  useGetDirectSaleVariantQuery,
  useGetMaterialPriceTypesQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  useGetWarehousesQuery,
  useGetWorkRateTypesQuery,
  usePatchDirectSaleMutation,
  usePostPrintoutOnDemandMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import {CustomNoticeList, useCustomNoticesBadge} from '@dms/features/custom-notices';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  CheckoutIssuingProvider,
  Checklists,
  DetailTemplate,
  DetailTemplateHeader,
  getUserName,
  handleApiError,
  NavigationItem,
  queryParams,
  createHeaderActions,
  Section,
  useChecklist,
  usePermissions,
  printPDF,
  useFileNotification,
  refreshDmsDatagrid,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';
import {createHeaderFlags, isNonEditableDirectSaleState} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  generateHashFromObjects,
  getRandomId,
  isNilOrZero,
  Nullish,
  parseDate,
  useBoolean,
  useNavigate,
  useQueryState,
} from 'shared/web';

import {Customer} from './(sections)/Customer/Customer';
import {Overview} from './(sections)/DirectSale/Overview';
import {TABS_IDS} from './(sections)/DirectSale/utils/tabsIds';
import {Documents} from './(sections)/Documents/Documents';
import {openBulkRecalculationDialog} from './(sections)/Vehicle/openBulkRecalculationDialog';
import {Vehicle} from './(sections)/Vehicle/Vehicle';
import {DirectSaleActivityPanel} from './components/DirectSaleActivityPanel/DirectSaleActivityPanel';
import {DirectSaleMaterialReturnModal} from './components/DirectSaleMaterialReturnModal';

const DIRECT_SALE_SECTION_IDS = {
  directSale: 'directSale',
  documents: 'documents',
  vehicle: 'vehicle',
  checklists: 'checklists',
  customNotices: 'customNotices',
};

const PERCENTAGE_BASE = 100;

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export function DirectSalesDetail() {
  const {directSaleId} = useWarehouseParams();

  const fileNotification = useFileNotification({onTimeout: () => showNotification.error()});

  const navigate = useNavigate();

  const currencyFormatter = useFormatCurrency();

  const formatDate = useDateTimeFormatter();

  // Kept here, not in DetailTemplate: the template remounts whenever the direct sale changes. The
  // user setting keeps the choice across records, reloads and devices.
  const {data: isHeaderCollapsed = false, isLoading: isHeaderCollapsedLoading} =
    useGetRecordHeaderCollapsedQuery();
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const [setRecordHeaderCollapsed, {isLoading: isSavingHeaderCollapsed}] =
    useSetRecordHeaderCollapsedMutation();

  const handleHeaderCollapsedChange = (isCollapsed: boolean) => {
    // A click during a save is ignored: overlapping saves would roll back to a value nobody saved.
    if (isNil(currentUser) || isSavingHeaderCollapsed) {
      return;
    }

    setRecordHeaderCollapsed({userId: currentUser.id, isCollapsed}).unwrap().catch(handleApiError);
  };
  const [activeSectionId, setActiveSectionId] = useQueryState(
    queryParams.COMPONENT_SECTIONS_SECTION
  );

  const [, setActiveTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);

  const [isMaterialReturnModalVisible, openMaterialReturnModal, closeMaterialReturnModal] =
    useBoolean();

  const [directSaleEdit, canReadTasks] = usePermissions({
    permissionKeys: ['directSaleEdit', 'readTask'],
  });

  const {count: tasksTodoCount} = useTasksCount({
    entityId: directSaleId,
    relation: EntityResourceIds.directSale,
    state: 'TODO',
  });

  const {count: tasksInProgressCount} = useTasksCount({
    entityId: directSaleId,
    relation: EntityResourceIds.directSale,
    state: 'IN_PROGRESS',
  });

  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const {customNoticesBadge} = useCustomNoticesBadge({
    resourceId: EntityResourceIds.directSale,
    recordId: directSaleId,
  });

  const {
    data: directSale,
    isLoading: isDirectSaleLoading,
    isFetching: isDirectSaleFetching,
    isError: hasDirectSaleError,
  } = useGetDirectSaleQuery(
    {directSaleId: directSaleId as string},
    {skip: isNilOrEmpty(directSaleId)}
  );

  const {
    data: availableWarehouses,
    isLoading: isAvailableWarehousesLoading,
    isError: isAvailableWarehousesError,
  } = useGetWarehousesQuery(
    {directSaleVariantId: defaultTo('', directSale?.directSaleVariantId)},
    {skip: isNil(directSale)}
  );

  const {
    data: directSaleVariant,
    isLoading: isDirectSaleVariantLoading,
    isError: hasDirectSaleVariantError,
  } = useGetDirectSaleVariantQuery(
    {directSaleVariantId: defaultTo('', directSale?.directSaleVariantId)},
    {skip: isNil(directSale)}
  );

  const {data: user, isLoading: isUserLoading} = useGetUserQuery({id: directSale?.createdBy ?? ''});

  const {
    data: directSaleActions,
    isLoading: isDirectSaleActionsLoading,
    isError: hasDirectSaleActionsError,
  } = useGetWarehouseHeaderDynamicActionsQuery({id: directSaleId, resource: 'direct-sale'});

  const {data: tenant, isLoading: isTenantLoading, isError: hasTenantError} = useGetTenantQuery();

  const {
    data: authorizationProfiles,
    isLoading: isAuthorizationProfilesLoading,
    isError: hasAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});

  const {
    data: branches,
    isLoading: areBranchesLoading,
    isError: hasBranchesError,
  } = useGetBranchListQuery();

  const {
    data: directSaleTypes,
    isLoading: isDirectSaleTypesLoading,
    isError: hasDirectSaleTypesError,
  } = useGetDirectSaleTypesQuery();

  const {
    data: materialPriceTypes,
    isLoading: isMaterialPriceTypesLoading,
    isError: hasMaterialPriceTypesError,
  } = useGetMaterialPriceTypesQuery();

  const {
    data: labourRateTypes,
    isLoading: isLabourRateTypesLoading,
    isError: hasLabourRateTypesError,
  } = useGetWorkRateTypesQuery();

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'direct-sale', contextId: directSaleId},
    {skip: !directSaleId}
  );

  const [saveDirectSale] = usePatchDirectSaleMutation();
  // Separate hook instance so only vehicle saves (which can bulk-recalculate item prices
  // on the backend) lock the page - customer changes stay lock-free.
  const [saveDirectSaleVehicle, {isLoading: isSavingVehicle}] = usePatchDirectSaleMutation();

  const [putDirectSaleAction, {isLoading: isPutDirectSaleActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation();

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'direct-sale',
    contextId: directSaleId,
  });

  const isLoading =
    isDirectSaleLoading ||
    isDirectSaleVariantLoading ||
    isUserLoading ||
    isAvailableWarehousesLoading ||
    isDirectSaleActionsLoading ||
    isTenantLoading ||
    isAuthorizationProfilesLoading ||
    areBranchesLoading ||
    isDirectSaleTypesLoading ||
    isMaterialPriceTypesLoading ||
    isLabourRateTypesLoading ||
    isHeaderCollapsedLoading;

  const isError =
    hasDirectSaleError ||
    hasDirectSaleVariantError ||
    hasDirectSaleActionsError ||
    isAvailableWarehousesError ||
    hasTenantError ||
    hasAuthorizationProfilesError ||
    hasBranchesError ||
    hasDirectSaleTypesError ||
    hasMaterialPriceTypesError ||
    hasLabourRateTypesError;

  const directSaleTitle = `${i18n.t('page.warehouse.labels.directSale')} ${
    directSale?.number ?? ''
  }`;

  const priceWithVat = currencyFormatter(
    directSale?.totalPrice.withVat.amount ?? 0,
    directSale?.totalPrice.withVat.currency ?? '',
    2
  );

  const priceWithoutVat = currencyFormatter(
    directSale?.totalPrice.withoutVat.amount ?? 0,
    directSale?.totalPrice.withoutVat.currency ?? '',
    2
  );

  const hasPendingReturn = !!directSale?.hasPendingReturn;

  const formatToShortDate = (date?: string) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return formatDate('dateShort', parseDate(date));
  };

  const createdAt = `${i18n.t('general.labels.dateCreated')}: ${formatToShortDate(directSale?.createdAt)}`;

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(user) ?? EMPTY_PLACEHOLDER}`;

  const isEditingDisabled = isNonEditableDirectSaleState(directSale?.state);

  const handleChangeDirectSaleVariant = () => {
    navigate(
      composePath(warehouseRoutes.directSalesVariantChange, {
        params: {id: directSaleId, directSaleVariantId: directSale?.directSaleVariantId},
      })
    );
  };

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'prntDirectSaleOffer') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: directSaleId,
          originEntityPrintoutType: 'prntDirectSaleOffer',
        },
      })
        .unwrap()
        .then((data) => {
          const fileId = data?.printout?.fileId;
          const pdfUrl = data?.printout?.pdfUrl;

          if (fileId) {
            fileNotification.registerFile(fileId, {
              onSuccess: () => {
                if (pdfUrl) {
                  printPDF(pdfUrl);
                } else {
                  showNotification.error();
                }
              },
              onError: () => showNotification.error(),
            });
          } else {
            showNotification.error();
          }
        })
        .then(refreshDocumentsCount)
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.directSalesDetailDocuments, {
              params: {id: directSaleId},
            })
          );
        })
        .catch(handleApiError);
    }

    return (
      match(actionKey)
        .with('return', () => openMaterialReturnModal())
        .with('discount', () => {
          setActiveTabId(TABS_IDS.discountTab);
        })
        .with('variantChange', () => handleChangeDirectSaleVariant())
        // Confirmation is handled centrally by createHeaderActions; returning the
        // promise keeps the delete dialog open with a loading state until it settles
        .with('delete', () =>
          putDirectSaleAction({id: directSaleId, resource: 'direct-sale', body: {actionKey}})
            .unwrap()
            // Non-empty options force same-tab navigation while the confirm
            // chord's Ctrl/⌘ may still be held (useNavigate would window.open)
            .then(() => navigate(composePath(warehouseRoutes.directSalesList), {replace: false}))
            .catch(handleApiError)
        )
        .with('offer', () =>
          putDirectSaleAction({id: directSaleId, resource: 'direct-sale', body: {actionKey}})
            .unwrap()
            .then(refreshDocumentsCount)
            .catch(handleApiError)
        )
        .otherwise(() => {
          const confirmText = match(actionKey)
            .with(
              'lock',
              always(`${i18n.t('page.warehouse.labels.lockDirectSale')} ${directSale?.number}?`)
            )
            .otherwise(always(null));

          if (isNil(confirmText)) {
            return putDirectSaleAction({
              id: directSaleId,
              resource: 'direct-sale',
              body: {actionKey},
            })
              .unwrap()
              .catch(handleApiError);
          }

          openConfirmDialog({
            text: confirmText,
            onConfirm: () =>
              putDirectSaleAction({id: directSaleId, resource: 'direct-sale', body: {actionKey}})
                .unwrap()
                .catch(handleApiError),
            'data-testid': testIds.warehouse.directSalesDetailOverview('dialogs'),
          });
        })
    );
  };

  const saveVehicle = (body: PatchDirectSaleRequest['body']) =>
    saveDirectSaleVehicle({directSaleId, body})
      .unwrap()
      .then(() =>
        showNotification.success(
          isNil(body.vehicleId)
            ? i18n.t('entity.warehouse.notifications.vehicleRemoved')
            : i18n.t('entity.warehouse.notifications.vehicleAssigned')
        )
      )
      .catch(handleApiError);

  const handleVehicleChange = async (vehicleId: string | Nullish) => {
    // Removing the vehicle, re-selecting the current one or an empty document never affects
    // existing prices - patch directly.
    if (
      isNil(vehicleId) ||
      vehicleId === directSale?.vehicleId ||
      isNilOrZero(directSale?.itemsCount)
    ) {
      return saveVehicle({vehicleId});
    }

    // Yield one task so dialog-close calls still pending in the caller's stack settle first
    // (Vehicle.handleSelectVehicle calls closeCurrentDialog right after this, without awaiting)
    // - otherwise that trailing close would dismiss the freshly opened prompt instead.
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Changing the vehicle on a non-empty document changes the labour rate context for all items.
    // Let the user decide how the prices for the existing items should be handled.
    const strategy = await openBulkRecalculationDialog({
      'data-testid': testIds.warehouse.directSalesDetailVehicle('dialogs.bulkRecalculation'),
    });

    // Dialog cancelled - revert the change by not applying the new vehicle.
    if (isNil(strategy)) {
      return;
    }

    return saveVehicle({vehicleId, isItemPriceUpdate: strategy === 'update'});
  };

  const handleCustomerChange = (customerId: string | Nullish) =>
    saveDirectSale({directSaleId, body: {customerId}})
      .unwrap()
      .then(() =>
        showNotification.success(
          isNil(customerId)
            ? i18n.t('entity.warehouse.notifications.customerRemoved')
            : i18n.t('entity.warehouse.notifications.customerAssigned')
        )
      )
      .catch(handleApiError);

  const handleCloseMaterialReturnModal = () => {
    closeMaterialReturnModal();
    refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseDirectSaleItem);
  };

  const getAuthorizationProfileFromVariant = (authorizationProfileId: string | Nullish) => {
    const result = authorizationProfiles?.find((authorizationProfile) =>
      equals(authorizationProfile.id, authorizationProfileId)
    );

    return defaultTo(EMPTY_PLACEHOLDER, result?.name);
  };

  const getBranchFromDirectSale = (branchId: string | Nullish) => {
    const result = branches?.branchListItems?.find((branch) => equals(branch.id, branchId));

    return defaultTo(EMPTY_PLACEHOLDER, result?.marketingName);
  };

  const getDirectSaleTypeFromVariant = (directSaleTypeId: string | Nullish) => {
    const result = directSaleTypes?.find((directSaleType) =>
      equals(directSaleType?.directSaleTypeId, directSaleTypeId)
    );

    return defaultTo(EMPTY_PLACEHOLDER, result?.name);
  };

  const getPriceTypeFromVariant = (code: string | Nullish, type: 'material' | 'labour') =>
    match(type)
      .with('material', () => {
        const result = materialPriceTypes?.find((materialPriceType) =>
          equals(materialPriceType?.code, code)
        );
        return defaultTo(EMPTY_PLACEHOLDER, result?.name);
      })
      .with('labour', () => {
        const result = labourRateTypes?.find((labourRateType) =>
          equals(labourRateType?.code, code)
        );
        return defaultTo(EMPTY_PLACEHOLDER, result?.name);
      })
      .exhaustive();

  const getMarginFromVariant = (margin: number | Nullish) =>
    margin ? `${margin * PERCENTAGE_BASE} %` : EMPTY_PLACEHOLDER;

  const getRatioFromVariant = (ratio: number | Nullish) =>
    ratio ? ratio * PERCENTAGE_BASE : EMPTY_PLACEHOLDER;

  const dynamicFlags = createHeaderFlags(directSale?.markers);

  const staticFlags = match(directSale?.state)
    .returnType<Omit<BaseFlagProps, 'size'>[]>()
    .with(
      'LOCKED',
      always([
        {
          colorScheme: 'green',
          label: i18n.t('entity.warehouse.labels.lockedState'),
        },
      ])
    )
    .with(
      'CLOSED',
      always([
        {
          colorScheme: 'green',
          label: i18n.t('entity.warehouse.labels.closedState'),
        },
      ])
    )
    .with(
      'ARCHIVED',
      always([
        {
          colorScheme: 'neutral',
          label: i18n.t('entity.warehouse.labels.archivedState'),
        },
      ])
    )
    .with(
      'CORRECTING_FULLY',
      always([
        {
          colorScheme: 'orange',
          label: i18n.t('entity.warehouse.labels.correctingFullyState'),
        },
      ])
    )
    .with(
      'CORRECTING_PARTIALLY',
      always([
        {
          colorScheme: 'yellow',
          label: i18n.t('entity.warehouse.labels.correctingPartiallyState'),
        },
      ])
    )
    .otherwise(
      always([
        {
          colorScheme: 'blue',
          label: i18n.t('entity.warehouse.labels.openState'),
        },
      ])
    );

  const headerFlags = concatAll<FlagProps[]>([staticFlags, dynamicFlags]);

  const headerActions = createHeaderActions({
    actions: directSaleActions?.actions,
    callback: handleActionClick,
    isLoading: isPutDirectSaleActionLoading || isPostPrintoutOnDemandLoading,
  });

  const variantTooltipData = [
    {
      label: i18n.t('entity.warehouse.labels.authorizationProfile'),
      value: getAuthorizationProfileFromVariant(directSaleVariant?.general?.authorizationProfileId),
    },
    {
      label: i18n.t('entity.warehouse.labels.branch'),
      value: getBranchFromDirectSale(directSale?.branchId),
    },
    {
      label: i18n.t('entity.warehouse.labels.directSaleType'),
      value: getDirectSaleTypeFromVariant(directSaleVariant?.general?.directSaleTypeId),
    },
    {
      label: i18n.t('entity.warehouse.labels.unitPriceWithVAT'),
      value: isFalsy(directSaleVariant?.general?.isUnitPriceWithVat)
        ? i18n.t('general.labels.no')
        : i18n.t('general.labels.yes'),
    },
    {
      label: i18n.t('entity.warehouse.labels.materialPriceType'),
      value: getPriceTypeFromVariant(directSaleVariant?.general?.priceType, 'material'),
    },
    {
      label: i18n.t('entity.warehouse.labels.minimumMaterialMargin'),
      value: getMarginFromVariant(directSaleVariant?.general?.minimalMaterialMargin),
    },
    {
      label: i18n.t('entity.warehouse.labels.materialPriceTypeRatio'),
      value: getRatioFromVariant(directSaleVariant?.general?.priceTypeRatio),
    },
    {
      label: i18n.t('entity.warehouse.labels.labourRateType'),
      value: getPriceTypeFromVariant(directSaleVariant?.general?.rateType, 'labour'),
    },
    {
      label: i18n.t('entity.warehouse.labels.minimumLabourMargin'),
      value: getMarginFromVariant(directSaleVariant?.general?.minimalWorkMargin),
    },
    {
      label: i18n.t('entity.warehouse.labels.labourRatio'),
      value: getRatioFromVariant(directSaleVariant?.general?.rateTypeRatio),
    },
    {
      label: i18n.t('entity.warehouse.labels.note'),
      value: defaultTo(EMPTY_PLACEHOLDER, directSaleVariant?.general?.note),
    },
  ];

  const variantTooltipContent: ReactNode = (
    <>
      {variantTooltipData.map((tooltipItem, index) => {
        const isFirstItem = index === 0;

        return (
          <Fragment key={`tooltipItem-${getRandomId()}`}>
            <Show when={not(isFirstItem)}>
              <Space vertical={1} />
            </Show>

            <HStack spacing={1}>
              <Text size="xSmall" color="white">
                {`• ${tooltipItem.label}:`}
              </Text>
              <Text size="xSmall" color="white" alternative>
                {tooltipItem.value}
              </Text>
            </HStack>
          </Fragment>
        );
      })}
    </>
  );

  const header: DetailTemplateHeader = {
    title: directSaleTitle,
    isEntityLoading: isDirectSaleFetching,
    icon: 'action/shopping_cart',
    parameters: [
      directSale?.number,
      {
        title: directSale?.variantName,
        tooltip: variantTooltipContent,
        shouldShowTooltipIcon: true,
        hasAutoWidth: true,
      },
      createdAt,
      createdBy,
    ],
    recordId: directSale?.id,
    resourceId: EntityResourceIds.directSales,
    controls: ['ASSIGNEE'],
    flags: headerFlags,
    actions: headerActions,
    primaryParameter: defaultTo(undefined, priceWithVat),
    secondaryParameter: `${priceWithoutVat} ${i18n.t('general.labels.w/oVat')}`,
    alerts: buildArray<AlertProps>()
      .when(hasPendingReturn, {
        title: i18n.t('entity.warehouse.notifications.youHavePendingItemsForReturn'),
        hyperlinks: [
          {
            title: i18n.t('entity.warehouse.actions.showPending'),
            onClick: openMaterialReturnModal,
          },
        ],
      })
      .whenNot(directSaleEdit, {
        type: 'banner',
        variant: 'info',
        message: i18n.t('module.warehouse.label.missingDirectSaleEditPermission'),
      })
      .when(isPositive(directSale?.marginUnderMinimumCount), {
        type: 'banner',
        variant: 'warning',
        message: i18n.t('entity.warehouse.labels.marginUnderMinimumWarning'),
      }),
  };

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: directSaleId || '',
    resourceId: 'DIRECT_SALE',
  });

  const navigation = buildArray<NavigationItem>([
    {
      id: DIRECT_SALE_SECTION_IDS.directSale,
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.directSalesDetailOverview, {
        params: {id: directSaleId},
      }),
      content: (
        <Overview
          maximization={{enabled: true, title: directSaleTitle}}
          directSale={directSale as GetDirectSaleResponse}
          isDirectSaleLoading={isDirectSaleLoading}
          isEditingDisabled={isEditingDisabled}
          directSaleActions={directSaleActions?.actions}
          availableWarehouses={availableWarehouses as GetWarehousesResponse}
          onMaterialReturnModalOpen={openMaterialReturnModal}
          data-testid={testIds.warehouse.directSalesDetailOverview('section.directSale')}
        />
      ),
      'data-testid': testIds.warehouse.directSalesDetailOverview('navigation.directSale'),
    },
    {
      id: DIRECT_SALE_SECTION_IDS.documents,
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.directSalesDetailDocuments, {
        params: {id: directSaleId},
      }),
      content: (
        <Documents
          maximization={{enabled: true, title: directSaleTitle}}
          onDocumentAction={refreshDocumentsCount}
          data-testid={testIds.warehouse.directSalesDetailDocuments('section.documents')}
        />
      ),
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.directSalesDetailDocuments('navigation.documents'),
    },
    {
      id: DIRECT_SALE_SECTION_IDS.vehicle,
      label: i18n.t('page.warehouse.labels.vehicle'),
      href: composePath(warehouseRoutes.directSalesDetailVehicle, {
        params: {id: directSaleId},
      }),
      content: (
        <Vehicle
          vehicleId={defaultTo('', directSale?.vehicleId)}
          isDirectSaleLoading={isDirectSaleLoading}
          isEditingDisabled={isEditingDisabled}
          onVehicleChange={handleVehicleChange}
          data-testid={testIds.warehouse.directSalesDetailVehicle('section.vehicle')}
        />
      ),
      'data-testid': testIds.warehouse.directSalesDetailVehicle('navigation.vehicle'),
    },
    {
      id: 'customer',
      label: i18n.t('page.warehouse.labels.customer'),
      href: composePath(warehouseRoutes.directSalesDetailCustomer, {
        params: {id: directSaleId},
      }),
      content: (
        <Customer
          customerId={defaultTo('', directSale?.customerId)}
          isDirectSaleLoading={isDirectSaleLoading}
          isEditingDisabled={isEditingDisabled}
          onCustomerChange={handleCustomerChange}
          data-testid={testIds.warehouse.directSalesDetailCustomer('section.customer')}
        />
      ),
      'data-testid': testIds.warehouse.directSalesDetailCustomer('navigation.customer'),
    },
  ])
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: DIRECT_SALE_SECTION_IDS.checklists,
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(warehouseRoutes.directSalesDetailChecklists, {
        params: {id: directSaleId},
      }),
      content: (
        <Section>
          <Checklists {...checklistSettings} contextTarget="direct-sale" contextId={directSaleId} />
        </Section>
      ),
      badge: badgeSettings,
      'data-testid': testIds.warehouse.directSalesDetailChecklists('checklists'),
    })
    .when(canReadTasks, {
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      href: composePath(warehouseRoutes.directSalesDetailTasks, {
        params: {id: directSaleId},
      }),
      'data-testid': testIds.warehouse.directSalesDetailOverview('navigation-tasks'),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
      content: (
        <TaskGridPage
          maximization={{enabled: true, title: directSaleTitle}}
          gridCode="direct-sale-task"
          entityId={directSaleId}
          data-testid={testIds.warehouse.directSalesDetailOverview('tasks')}
        />
      ),
    })
    .whenFeatureEnabled(featureFlags.CUSTOM_NOTICES, {
      id: DIRECT_SALE_SECTION_IDS.customNotices,
      label: i18n.t('general.customNotices.labels.title'),
      href: composePath(warehouseRoutes.directSalesDetailCustomNotices, {
        params: {id: directSaleId},
      }),
      content: (
        <CustomNoticeList
          resourceId={EntityResourceIds.directSale}
          recordId={directSaleId}
          data-testid={testIds.warehouse.directSalesDetailCustomNotices('customNotices')}
        />
      ),
      badge: customNoticesBadge,
      'data-testid': testIds.warehouse.directSalesDetailCustomNotices('navigation.customNotices'),
    });
  return (
    <CheckoutIssuingProvider>
      <>
        <Helmet title={i18n.t('page.warehouse.labels.directSaleDetail')} />
        <DetailTemplate
          key={generateHashFromObjects(directSale)}
          isLoading={isLoading}
          isError={isError}
          isLocked={isPutDirectSaleActionLoading || isSavingVehicle}
          header={header}
          navigation={navigation}
          activityPanel={<DirectSaleActivityPanel directSaleId={directSaleId} />}
          data-testid={testIds.warehouse.directSalesDetailOverview('page')}
          activeSectionId={activeSectionId}
          onSectionChange={setActiveSectionId}
          headerCollapse={{
            isCollapsed: isHeaderCollapsed,
            onCollapsedChange: handleHeaderCollapsedChange,
          }}
        />

        <Show when={isMaterialReturnModalVisible}>
          <DirectSaleMaterialReturnModal
            directSaleId={directSaleId}
            authorizationProfileId={directSale?.authorizationProfileId}
            onClose={handleCloseMaterialReturnModal}
            data-testid={testIds.warehouse.directSalesDetailOverview('dialogs.returnMaterial')}
          />
        </Show>
      </>
    </CheckoutIssuingProvider>
  );
}
